import { useCallback, useMemo, useRef, useState } from 'react';
import {
  AnimatedPopover,
  Content,
  Icon,
  Inline,
  InlineItem,
  Menu,
  MenuItem,
  Text,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { Place } from '@/types';
import { flattenPlaceTree } from '@/data/places';

type PlaceOptionProps = {
  label: string;
  depth: number;
  selected: boolean;
  onClick: () => void;
};

// One row in the place-scope popover: an indented label (depth in the
// hierarchy) with a check on the current scope.
const PlaceOption = (props: PlaceOptionProps) => {
  const { label, depth, selected, onClick } = props;
  return (
    <MenuItem
      onClick={onClick}
      content={
        <Inline>
          <InlineItem sizing='fill-container'>
            <div style={{ paddingLeft: `${depth * 16}px` }}>
              <Text>{label}</Text>
            </div>
          </InlineItem>
          {selected && (
            <InlineItem sizing='fit-content'>
              <Icon magnitude='small' type='check' />
            </InlineItem>
          )}
        </Inline>
      }
    />
  );
};

// Place-scope editor: a config row showing the current scope (the scoped
// place's name, or "Whole inventory" when unset) that opens a popover
// listing "Whole inventory" + the inventory's place hierarchy (indented by
// depth). Picking an entry scopes the surface to that place's subtree;
// "Whole inventory" clears the scope. Shared by the disclosure header and
// the pathway sidebar — the same place-scope-as-query concept. Extracted
// from `disclosure-header` when the second consumer landed.
const PlaceMenuItem = (props: PlaceMenuItemProps) => {
  const { places, value, onChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const flat = useMemo(() => flattenPlaceTree(places), [places]);
  const selected = useMemo(
    () =>
      value !== undefined ? places.find((p) => p.id === value) : undefined,
    [places, value],
  );

  const pick = useCallback(
    (place: number | undefined) => {
      onChange(place);
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<Icon magnitude='medium' type='map-filled' />}
        content={<Text>{selected ? selected.name : 'Whole inventory'}</Text>}
        onClick={toggle}
      />
      <AnimatedPopover
        stopPropagation
        justify='stretch'
        open={isOpen}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='small'
        >
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <Menu>
              <PlaceOption
                label='Whole inventory'
                depth={0}
                selected={value === undefined}
                onClick={() => pick(undefined)}
              />
              {flat.map(({ place, depth, key }) => (
                <PlaceOption
                  key={key}
                  label={place.name}
                  depth={depth + 1}
                  selected={place.id === value}
                  onClick={() => pick(place.id)}
                />
              ))}
            </Menu>
          </div>
        </Content>
      </AnimatedPopover>
    </>
  );
};

export type PlaceMenuItemProps = {
  places: Place[];
  value: number | undefined;
  onChange: (place: number | undefined) => void;
};

export { PlaceMenuItem };
